import { computed, type Ref } from "vue";
import type { ProductQtyBreak } from "~/layers/base/stores/productAttributes";
import {
	calculateDiscountPercent,
	deriveFallbackOldPrice,
	parseQty
} from "~/templates/breadandboxers/domain/pricing";

export interface MultipackOption {
	qtyBreak: number;
	labelQuantity: number;
	cartQuantity: number;
	unitPrice: number;
	oldUnitPrice: number | null;
	discountPercent: number;
}

type ProductQtyBreakWithOldPrice = ProductQtyBreak & {
	OldPrice?: { ValueIncVat?: number | null };
};

/*
	`PriceRecommended` used to close this chain and no longer does.

	Norce's definition of it is a price the merchant sets, usually the supplier's
	recommended one, never calculated from anything. Striking it through with a
	saving attached announces a reduction against a price that was never charged,
	which is what the Omnibus directive forbids — and it is why the same product
	read as -57% here while its own page showed no discount at all.

	What is left are the two that are genuinely prices this line was reduced
	from: `OldPrice` for the break itself, then whatever the caller passes as the
	fallback.
*/
const getOldUnitPrice = (
	breakItem: ProductQtyBreakWithOldPrice,
	baseUnitPrice: number,
	fallback: number | null
) => {
	const old = breakItem.OldPrice?.ValueIncVat;

	if (typeof old === "number" && old > 0) {
		return old;
	}

	return deriveFallbackOldPrice({
		unitPrice: breakItem.ProductPrice.ValueIncVat ?? baseUnitPrice,
		baseUnitPrice,
		fallback
	});
};

export const useMultipackPricing = (params: {
	quantityBreaks: Ref<ProductQtyBreak[] | undefined>;
	baseUnitPrice: Ref<number>;
	basePackSize: Ref<number>;
	productOldUnitPrice: Ref<number | null>;
}) => {
	const options = computed<MultipackOption[]>(() => {
		const breaks = params.quantityBreaks.value;

		if (!breaks?.length) {
			return [];
		}

		const mapped = breaks
			.map((breakItem) => {
				const qtyBreak = parseQty(breakItem.QtyBreak);
				if (!qtyBreak) {
					return null;
				}

				const packSize = Math.max(1, params.basePackSize.value);

				const unitPrice =
					breakItem.ProductPrice.ValueIncVat ?? params.baseUnitPrice.value;
				const oldUnitPrice = getOldUnitPrice(
					breakItem as ProductQtyBreakWithOldPrice,
					params.baseUnitPrice.value,
					params.productOldUnitPrice.value
				);

				return {
					qtyBreak,
					labelQuantity: qtyBreak * packSize,
					cartQuantity: qtyBreak,
					unitPrice,
					oldUnitPrice,
					discountPercent: calculateDiscountPercent(oldUnitPrice, unitPrice)
				};
			})
			.filter((option): option is MultipackOption => option !== null);

		return [...mapped].sort((a, b) => a.qtyBreak - b.qtyBreak);
	});

	return {
		options
	};
};
